export type DirectState = {
  directPhase: "idle" | "ready" | "playing" | "paused" | "blocked";
  directSeconds: number;
  directReady: boolean;
  directError: string;
};

function silence() {
  const data = new ArrayBuffer(1644),
    view = new DataView(data);
  const word = (offset: number, value: string) =>
    [...value].forEach((c, i) => view.setUint8(offset + i, c.charCodeAt(0)));
  word(0, "RIFF");
  view.setUint32(4, 1636, true);
  word(8, "WAVE");
  word(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, 8000, true);
  view.setUint32(28, 16000, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  word(36, "data");
  view.setUint32(40, 1600, true);
  return new Blob([data], { type: "audio/wav" });
}

/** One persistent native media element, independent of the Web Audio routing/filter. */
export class DirectSpeech {
  private element: HTMLAudioElement | null = null;
  private url?: string;
  private released = false;
  private serial = 0;
  private finish?: (error?: Error) => void;
  private speaking = false;
  private state: DirectState = {
    directPhase: "idle",
    directSeconds: 0,
    directReady: false,
    directError: "",
  };
  constructor(
    private notify: (state: DirectState) => void,
    private onSpeakingChange: (speaking: boolean) => void = () => {},
  ) {}
  private setSpeaking(speaking: boolean) {
    if (speaking === this.speaking) return;
    this.speaking = speaking;
    this.onSpeakingChange(speaking);
  }
  get attached() {
    return !!this.element;
  }
  private update(value: Partial<DirectState>) {
    this.state = { ...this.state, ...value };
    this.notify(this.state);
  }
  attach(element: HTMLAudioElement | null) {
    if (element === this.element) return;
    this.dispose();
    this.element = element;
    this.released = false;
    if (!element) return;
    element.onplaying = () => {
      this.released = true;
      if (this.state.directReady) this.setSpeaking(true);
      this.update({ directPhase: "playing", directError: "" });
    };
    element.onpause = () => {
      if (this.state.directReady && element.paused) {
        this.setSpeaking(false);
        if (!element.ended) this.update({ directPhase: "paused" });
      }
    };
    element.ontimeupdate = () => {
      if (this.state.directReady)
        this.update({
          directSeconds: Math.floor(element.currentTime * 10) / 10,
        });
    };
    element.onended = () => {
      this.setSpeaking(false);
      this.update({ directPhase: "ready" });
      this.finish?.();
    };
    element.onerror = () => {
      this.setSpeaking(false);
      this.finish?.(
        Error("Die Sprachaufnahme konnte nicht abgespielt werden."),
      );
    };
  }
  async unlock(volume: number) {
    const element = this.element;
    if (!element) return false;
    element.volume = volume;
    element.muted = false;
    if (this.released) return true;
    if (!this.state.directReady) {
      this.replaceUrl(silence());
      element.src = this.url!;
    }
    const serial = this.serial;
    try {
      await element.play();
      if (serial !== this.serial) return false;
      this.released = true;
      if (!this.state.directReady) {
        element.pause();
        this.update({ directPhase: "idle" });
      }
      return true;
    } catch {
      return false;
    }
  }
  private replaceUrl(blob: Blob) {
    if (this.url) URL.revokeObjectURL(this.url);
    this.url = URL.createObjectURL(blob);
  }
  play(wav: Blob, volume: number) {
    this.stop();
    const element = this.element;
    if (!element) return Promise.reject(Error("Audioplayer ist nicht bereit."));
    this.replaceUrl(wav);
    element.src = this.url!;
    element.volume = volume;
    element.muted = false;
    this.update({
      directReady: true,
      directPhase: "ready",
      directSeconds: 0,
      directError: "",
    });
    const serial = this.serial;
    // Acquire before play(), release on rejection/pause/end, including native controls.
    this.setSpeaking(true);
    return new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        element.pause();
        this.finish?.(
          Error("Sprachwiedergabe pausiert. Aufnahme unten erneut starten."),
        );
      }, 120000);
      this.finish = (error?: Error) => {
        clearTimeout(timeout);
        this.finish = undefined;
        this.setSpeaking(false);
        error ? reject(error) : resolve();
      };
      void element.play().catch((error: Error) => {
        if (serial !== this.serial) return;
        this.setSpeaking(false);
        if (error.name === "NotAllowedError") {
          this.released = false;
          this.update({
            directPhase: "blocked",
            directError:
              "Browser blockiert die Wiedergabe. Bitte ▶ im Audioplayer unten drücken.",
          });
          // Keep this exact recording and wait for the built-in play control. No silent fallback.
        } else
          this.finish?.(
            Error(
              "Sprachaufnahme kann nicht abgespielt werden: " + error.message,
            ),
          );
      });
    });
  }
  volume(volume: number, enabled: boolean) {
    if (this.element) {
      this.element.volume = volume;
      this.element.muted = !enabled;
    }
  }
  stop() {
    this.serial++;
    this.element?.pause();
    this.setSpeaking(false);
    this.finish?.();
  }
  dispose() {
    this.stop();
    if (this.element) {
      this.element.onplaying = null;
      this.element.onpause = null;
      this.element.ontimeupdate = null;
      this.element.onended = null;
      this.element.onerror = null;
      this.element.removeAttribute("src");
      this.element.load();
    }
    if (this.url) URL.revokeObjectURL(this.url);
    this.url = undefined;
    this.element = null;
    this.released = false;
    this.update({
      directPhase: "idle",
      directReady: false,
      directSeconds: 0,
      directError: "",
    });
  }
}
